import { useState, useEffect, useCallback, useRef } from "react";
import {
  loadShopifyPrivacyApi,
  syncConsentToShopify,
  readShopifyConsent,
  type ConsentDecision,
  type ConsentCategory,
} from "@/lib/shopify-privacy";

/* ── constants ── */
const STORAGE_KEY = "motai_cookie_consent";
const EXPIRY_MONTHS = 12;

/* ── stored shape ── */
interface StoredConsent extends ConsentDecision {
  timestamp: string;
}

/* ── localStorage helpers ── */
function getStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredConsent = JSON.parse(raw);
    const stored = new Date(parsed.timestamp);
    const now = new Date();
    const diffMonths =
      (now.getFullYear() - stored.getFullYear()) * 12 +
      (now.getMonth() - stored.getMonth());
    if (diffMonths >= EXPIRY_MONTHS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(decision: ConsentDecision): StoredConsent {
  const stored: StoredConsent = {
    ...decision,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  return stored;
}

/* ── hook ── */
export function useCookieConsent() {
  const [consent, setConsent] = useState<StoredConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const initialised = useRef(false);

  /* Boot: load Shopify privacy script, then read stored / Shopify state */
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    loadShopifyPrivacyApi().then(() => {
      const stored = getStoredConsent();
      if (stored) {
        setConsent(stored);
        // Re-sync to Shopify in case it lost state (new session / cleared cookies)
        syncConsentToShopify(stored);
        setShowBanner(false);
      } else {
        // Check if Shopify already has a decision (e.g. from checkout)
        const shopify = readShopifyConsent();
        if (shopify && (shopify.analytics || shopify.marketing || shopify.preferences)) {
          const saved = saveConsent(shopify);
          setConsent(saved);
          setShowBanner(false);
        } else {
          setShowBanner(true);
        }
      }
    });
  }, []);

  /* ── actions ── */

  const applyDecision = useCallback((decision: ConsentDecision) => {
    const saved = saveConsent(decision);
    setConsent(saved);
    syncConsentToShopify(decision);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const acceptAll = useCallback(() => {
    applyDecision({ analytics: true, marketing: true, preferences: true });
  }, [applyDecision]);

  const rejectNonEssential = useCallback(() => {
    applyDecision({ analytics: false, marketing: false, preferences: false });
  }, [applyDecision]);

  const savePreferences = useCallback(
    (partial: ConsentDecision) => {
      applyDecision(partial);
    },
    [applyDecision]
  );

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  /** Check whether a specific category is consented. */
  const hasConsent = useCallback(
    (category: ConsentCategory): boolean => {
      if (!consent) return false; // no decision yet → block by default
      return consent[category];
    },
    [consent]
  );

  return {
    showBanner,
    showPreferences,
    consent,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
    hasConsent,
  };
}
